import { useTranslation } from 'react-i18next';

import { formatPercentageByLocale } from '@/utils/localeFormatters';

import styles from './CircularProgressBar.module.scss';

const cleanPercentage = (percentage: number) => {
    const isNegativeOrNaN = !Number.isFinite(+percentage) || percentage < 0; // we can set non-numbers to 0 here
    const isTooHigh = percentage > 100;
    return isNegativeOrNaN ? 0 : isTooHigh ? 100 : +percentage;
};

interface CircularProgressBarProps {
    percentage: number;
    /** Any CSS colour, a token's var() included: it is applied as a style, not an attribute. */
    color: string;
    size: number;
}

export function CircularProgressBar({ percentage, color, size = 150 }: CircularProgressBarProps) {
    const { i18n } = useTranslation();
    const pct = cleanPercentage(percentage);
    const r = size / 3;
    const innerRadius = size / 4;
    const circ = 2 * Math.PI * r;
    const strokePct = ((100 - pct) * circ) / 100;
    const fontSize = size / 7;
    const strokeWidth = size === 150 ? 16 : 10;

    return (
        <div>
            <svg width={size} height={size}>
                <g>
                    <circle
                        r={r}
                        cx="50%"
                        cy="50%"
                        fill="transparent"
                        // No track at 0%, where the dash offset spans the full circumference.
                        className={strokePct !== circ ? styles.track : undefined}
                        strokeWidth={`${strokeWidth}px`}
                        strokeDasharray={circ}
                        strokeDashoffset={0}
                    />
                    <circle
                        r={r}
                        cx="50%"
                        cy="50%"
                        fill="transparent"
                        // No colour at 0%, where the dash offset spans the full circumference.
                        style={strokePct !== circ ? { stroke: color } : undefined}
                        strokeWidth={`${strokeWidth}px`}
                        strokeDasharray={circ}
                        strokeDashoffset={strokePct}
                    />
                </g>
                <circle cx="50%" cy="50%" r={innerRadius} style={{ fill: color }} />
                <text
                    x="50%"
                    y="50%"
                    dominantBaseline="central"
                    textAnchor="middle"
                    fontSize={`${fontSize}px`}
                    className={styles.label}>
                    {formatPercentageByLocale(percentage, i18n.language, 1)}
                </text>
            </svg>
        </div>
    );
}
